import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import PopOverPrimitive, { usePopOverContext } from './PopOverPrimitive';
import * as styles from './Tooltip.module.scss';

const MIN_RATIO = 1 / 2;
const MAX_RATIO = 4 / 1;
const MAX_WIDTH = 120;

const checkAspectRatio =
  process.env.NODE_ENV === 'production'
    ? () => {}
    : (innerElement: HTMLDivElement) => {
        const clearTooltipError = () => {
          document.body.querySelector('.tooltip-error')?.remove();
        };

        const showTooltipError = (text: string) => {
          const error = document.createElement('div');
          error.className = 'tooltip-error';
          error.style.position = 'fixed';
          error.style.zIndex = '9999';
          error.style.top = '0';
          error.style.left = '0';
          error.style.width = '100%';
          error.style.backgroundColor = 'darkred';
          error.style.color = 'white';
          error.style.padding = '10px';
          error.style.textAlign = 'center';
          error.style.whiteSpace = 'pre-wrap';
          error.textContent = text;
          error.onclick = () => error.remove();
          document.body.appendChild(error);
        };

        const rect = innerElement.getBoundingClientRect();
        const text = innerElement.textContent;
        clearTooltipError();

        if (rect.width > MAX_WIDTH && text) {
          const ratio = rect.width / rect.height;

          if (ratio < MIN_RATIO || ratio > MAX_RATIO) {
            showTooltipError(
              `Check tooltip aspect ratio for tooltip: "${text.replace(
                `\n`,
                ' ',
              )}".\nTooltip is ${rect.width}px wide and ${
                rect.height
              }px high, ratio is ${ratio.toFixed(
                2,
              )}, but should be between ${MIN_RATIO} and ${MAX_RATIO}`,
            );
          }
        }
      };

const TooltipContent = ({ children }: { children: React.ReactNode }) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (contentRef.current) {
      checkAspectRatio(contentRef.current);
    }
  }, []);

  return (
    <PopOverPrimitive.Content ref={contentRef} className={styles.inner}>
      {children}
    </PopOverPrimitive.Content>
  );
};

type TooltipProps = React.ComponentProps<typeof PopOverPrimitive.Trigger> &
  Pick<
    React.ComponentProps<typeof PopOverPrimitive>,
    'enabledForSmallSize' | 'placement' | 'offset' | 'anchorRef'
  > & {
    children: React.ReactNode;
    noPortal?: boolean;
    content?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    danger?: boolean;
    trigger?: React.ComponentProps<typeof PopOverPrimitive>['trigger'];
    forceVisibleWhenReferenceHidden?: boolean;
    closeOnMouseDown?: boolean;
  };

const TooltipTrigger = (
  props: React.ComponentProps<typeof PopOverPrimitive.Trigger>,
) => {
  const { id } = usePopOverContext();
  return <PopOverPrimitive.Trigger aria-describedby={id} {...props} />;
};

const Tooltip = ({
  children,
  noPortal,
  content,
  danger,
  asChild,
  display,
  offset,
  forceVisibleWhenReferenceHidden,
  trigger = 'hover',
  closeOnMouseDown = true,
  ...props
}: TooltipProps) => {
  const hoverRef = useRef<{
    setHover: (idOrSetter: string | null) => void;
  } | null>(null);
  if (!content) {
    return <>{children}</>;
  }

  return (
    <PopOverPrimitive
      offset={(offset ?? 4) + 4}
      hoverGroupId="tooltip"
      hideWhenReferenceHidden={!forceVisibleWhenReferenceHidden}
      trigger={trigger}
      hoverRef={hoverRef}
      {...props}
    >
      <TooltipTrigger
        asChild={asChild}
        display={display}
        onMouseDown={
          trigger === 'hover' && closeOnMouseDown
            ? () => {
                hoverRef.current?.setHover(null);
              }
            : undefined
        }
      >
        {children}
      </TooltipTrigger>
      <PopOverPrimitive.Floating
        noPortal={noPortal}
        className={classNames({
          [styles.danger]: danger,
        })}
      >
        <TooltipContent>{content}</TooltipContent>
        <PopOverPrimitive.Arrow
          height={4}
          width={10}
          className={styles.arrow}
          viewBox="0 0 10 4"
        >
          <path d="M4.37531 0.499756L0 4L10 4L5.6247 0.499756C5.25948 0.207581 4.74052 0.207581 4.37531 0.499756Z" />
        </PopOverPrimitive.Arrow>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

interface MutedProps {
  className?: string;
  children: string | null;
}

Tooltip.Muted = ({ className, children }: MutedProps) => (
  <span className={classNames(styles.mutedText, className)}>{children}</span>
);

export default Tooltip;
